import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CartProvider } from './CartContext';
import { SellerDomainContext } from './SellerDomainContext';
import { ThemeContext } from './ThemeContext';
import { getSellerByDomain } from './marketApi';
import MarketNavbar from './MarketNavbar';
import Home from './pages/Home';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import TrackOrder from './pages/TrackOrder';
import SellerRegister from './pages/SellerRegister';
import SellerLogin from './pages/SellerLogin';
import SellerDashboard from './pages/SellerDashboard';
import AdminPanel from './pages/AdminPanel';
import './market.css';

function SellerFooter() {
  const sellerDomain = useContext(SellerDomainContext);
  if (!sellerDomain) return null;
  return (
    <footer style={{
      borderTop: '1px solid var(--border)',
      background: 'var(--surface)',
      padding: '20px 24px',
      textAlign: 'center',
    }}>
      <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>
          © {new Date().getFullYear()} {sellerDomain.business_name}. All rights reserved.
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          Sale is subjected to Terms, conditions and product warranty.
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          All legal disputes are subjected to seller jurisdiction.
        </div>
      </div>
    </footer>
  );
}

const MAIN_DOMAINS = ['marketplace.smabizz.com', 'web2.smabizz.com', 'www.smabizz.com', 'localhost', '127.0.0.1'];

export default function MarketApp() {
  const [sellerDomain, setSellerDomain] = useState(undefined);
  const [theme, setTheme] = useState(() => localStorage.getItem('smb_theme') || 'light');

  const toggleTheme = () => setTheme(t => {
    const next = t === 'light' ? 'dark' : 'light';
    localStorage.setItem('smb_theme', next);
    return next;
  });

  useEffect(() => {
    // ?previewDomain=shop.example.com — lets you test seller isolation without DNS changes
    const params = new URLSearchParams(window.location.search);
    const preview = params.get('previewDomain');
    const hostname = preview ? preview.toLowerCase() : window.location.hostname.toLowerCase();

    const isMainDomain = !preview && MAIN_DOMAINS.some(d => hostname === d);
    if (isMainDomain) {
      setSellerDomain(null);
      return;
    }
    getSellerByDomain(hostname).then(seller => setSellerDomain(seller || null));
  }, []);

  return (
    <div className={`market-root${theme === 'dark' ? ' dark' : ''}`}>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <SellerDomainContext.Provider value={sellerDomain ?? null}>
        <CartProvider>
          <BrowserRouter basename="/app/market">
            <MarketNavbar />
            <div style={{ flex: 1 }}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order/:ref" element={<OrderConfirmation />} />
                <Route path="/track" element={<TrackOrder />} />
                <Route path="/seller/register" element={<SellerRegister />} />
                <Route path="/seller/login" element={<SellerLogin />} />
                <Route path="/seller" element={<SellerDashboard />} />
                <Route path="/admin" element={<AdminPanel />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
            <SellerFooter />
          </BrowserRouter>
        </CartProvider>
      </SellerDomainContext.Provider>
      </ThemeContext.Provider>
    </div>
  );
}
